// Module 1 — unit-aware parameter input.
//
// The scenario document stores every value in SI. <ParamField> is the
// sole UI surface that ever converts. Props:
//
//   value       — SI value (number | null)
//   kind        — 'length' | 'speed' | 'altitude' | ...
//   unit        — display unit; omit to let the hook pick one
//   source      — 'preset' | 'user' | 'imported' | 'calibrated'
//   confidence  — 'validated' | 'provisional' | 'unverified'
//   bounds      — { min, max } in SI (optional)
//   citation    — short provenance note shown in the tooltip
//   required    — empty value flags "must fix" aria-describedby
//   readOnly    — hides the input chrome; badges still render
//   label / description / id / testId
//   onChange    — (siValue, displayUnit) => void
//
// Accessibility wiring: the <label>, <input>, the help description, and
// any error messaging are linked via aria-labelledby / aria-describedby.
// Badges carry aria-label so screen readers call them out.
import { useMemo, useState } from "react";
import { AlertTriangle, Check, Info, Layers, Wand2 } from "lucide-react";
import {
    Tooltip,
    TooltipContent,
    TooltipProvider,
    TooltipTrigger,
} from "@/components/ui/tooltip";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import useUnits from "@/hooks/useUnits";
import {
    defaultUnitFor,
    formatValue,
    fromDisplay,
    kindOf,
    toDisplay,
    unitsForKind,
} from "@/lib/units";

// ---------------------------------------------------------------------
// Badge set — matches the existing shadcn navy/teal chip conventions
// already used on PresetsPage.
// ---------------------------------------------------------------------
const SOURCE_META = {
    preset: {
        label: "Preset",
        aria: "Source: preset library",
        icon: Layers,
        cls: "bg-ux-accent/10 text-ux-accent",
    },
    user: {
        label: "User",
        aria: "Source: user edit",
        icon: Wand2,
        cls: "bg-emerald-500/15 text-emerald-300",
    },
    imported: {
        label: "Imported",
        aria: "Source: imported telemetry",
        icon: Info,
        cls: "bg-amber-500/15 text-amber-300",
    },
    calibrated: {
        label: "Calibrated",
        aria: "Source: calibration session",
        icon: Check,
        cls: "bg-indigo-500/15 text-indigo-300",
    },
};

const CONFIDENCE_META = {
    validated: {
        label: "Validated",
        aria: "Confidence: validated",
        cls: "bg-ux-success/10 text-ux-success border-ux-success/30",
    },
    provisional: {
        label: "Provisional",
        aria: "Confidence: provisional",
        cls: "bg-ux-warning/10 text-ux-warning border-ux-warning/30",
    },
    unverified: {
        label: "Unverified",
        aria: "Confidence: unverified",
        cls: "bg-ux-danger/10 text-ux-danger border-ux-danger/30",
    },
};

function SourceBadge({ source }) {
    const meta = SOURCE_META[source];
    if (!meta) return null;
    const Icon = meta.icon;
    return (
        <span
            className={`inline-flex items-center gap-1 rounded-full text-[10px] font-mono uppercase tracking-wider px-2 py-0.5 ${meta.cls}`}
            aria-label={meta.aria}
            data-testid={`paramfield-source-${source}`}
        >
            <Icon className="w-3 h-3" aria-hidden="true" />
            {meta.label}
        </span>
    );
}

function ConfidenceBadge({ confidence }) {
    const meta = CONFIDENCE_META[confidence];
    if (!meta) return null;
    return (
        <span
            className={`inline-flex items-center rounded-full border text-[10px] font-mono uppercase tracking-wider px-2 py-0.5 ${meta.cls}`}
            aria-label={meta.aria}
            data-testid={`paramfield-confidence-${confidence}`}
        >
            {meta.label}
        </span>
    );
}

// ---------------------------------------------------------------------
// ParamField
// ---------------------------------------------------------------------
export default function ParamField({
    id,
    label,
    description,
    value,          // SI
    kind,
    unit,           // optional override; otherwise unitForKind
    source,
    confidence,
    bounds,         // { min, max } in SI
    citation,
    required = false,
    readOnly = false,
    placeholder,
    onChange,
    testId,
}) {
    const { unitForKind } = useUnits();
    const effectiveUnit = unit || unitForKind(kind) || defaultUnitFor(kind);
    const rowId = id || testId || `pf-${kind}-${label || "field"}`;
    const helpId = `${rowId}-help`;
    const errId = `${rowId}-err`;

    const [localUnit, setLocalUnit] = useState(effectiveUnit);
    const activeUnit = unit || localUnit;

    // Display string. We only use the SI→display converter; the raw
    // input string is held transiently so the user can type "-0." etc.
    const displayStr = useMemo(() => {
        if (value === null || value === undefined || Number.isNaN(value)) return "";
        return formatValue(value, activeUnit);
    }, [value, activeUnit]);

    const [draft, setDraft] = useState(displayStr);
    const [focused, setFocused] = useState(false);

    // Keep the rendered string in sync with the SI value whenever we are
    // NOT actively editing. When focused, the user owns the string.
    if (!focused && draft !== displayStr) {
        // eslint-disable-next-line react-hooks/rules-of-hooks
        setDraft(displayStr);
    }

    const isEmpty = value === null || value === undefined || Number.isNaN(value);
    const missingRequired = required && isEmpty;
    const outOfBounds = useMemo(() => {
        if (isEmpty || !bounds) return null;
        if (bounds.min !== undefined && value < bounds.min) return "below";
        if (bounds.max !== undefined && value > bounds.max) return "above";
        return null;
    }, [value, bounds, isEmpty]);

    const hasError = missingRequired || outOfBounds != null;
    const errorText = missingRequired
        ? "This value is required."
        : outOfBounds === "below"
        ? `Below minimum (${formatValue(bounds.min, activeUnit)} ${activeUnit || ""}).`
        : outOfBounds === "above"
        ? `Above maximum (${formatValue(bounds.max, activeUnit)} ${activeUnit || ""}).`
        : "";

    const kindUnits = useMemo(() => unitsForKind(kind || kindOf(activeUnit)), [kind, activeUnit]);
    // Radix <SelectItem> requires a non-empty string value. The
    // dimensionless kind uses the empty string as its canonical SI
    // unit, so we hide the Select for kinds whose only option is the
    // empty string. (Unit-selector UX for "%" vs "ratio" comes in a
    // later module.)
    const showUnitSelector = !readOnly && kindUnits.some((u) => u.id !== "");

    const emit = (strValue, u) => {
        if (strValue.trim() === "") {
            onChange?.(null, u);
            return;
        }
        const parsed = Number(strValue);
        if (Number.isNaN(parsed)) return;
        const si = fromDisplay(parsed, u);
        onChange?.(si, u);
    };

    const handleUnitChange = (newUnit) => {
        // Convert the currently-displayed value into the new unit for a
        // seamless visual swap, then keep SI intact.
        setLocalUnit(newUnit);
        if (!isEmpty) {
            const nextDisplay = formatValue(value, newUnit);
            setDraft(nextDisplay);
        }
    };

    return (
        <div
            className="flex flex-col gap-1.5"
            data-testid={testId || `paramfield-${rowId}`}
        >
            {/* Row 1 — label + badges. */}
            <div className="flex items-center gap-2 flex-wrap">
                <label
                    id={`${rowId}-label`}
                    htmlFor={rowId}
                    className="text-[12px] text-ux-text-1 font-medium"
                >
                    {label}
                    {required && (
                        <span
                            className="ml-1 text-ux-danger"
                            aria-label="required"
                            title="Required"
                        >
                            *
                        </span>
                    )}
                </label>
                {source && <SourceBadge source={source} />}
                {confidence && <ConfidenceBadge confidence={confidence} />}
                {citation && (
                    <TooltipProvider delayDuration={120}>
                        <Tooltip>
                            <TooltipTrigger asChild>
                                <button
                                    type="button"
                                    className="inline-flex items-center justify-center w-4 h-4 rounded-full text-ux-text-3 hover:text-ux-text-1"
                                    aria-label={`Citation: ${citation}`}
                                    data-testid={`paramfield-citation-${rowId}`}
                                >
                                    <Info className="w-3.5 h-3.5" aria-hidden="true" />
                                </button>
                            </TooltipTrigger>
                            <TooltipContent
                                side="top"
                                className="max-w-[260px] bg-ux-bg-1 text-ux-text-1 border-ux-border-1 text-[12px]"
                            >
                                {citation}
                            </TooltipContent>
                        </Tooltip>
                    </TooltipProvider>
                )}
            </div>

            {/* Row 2 — input + unit selector. */}
            <div className="flex items-center gap-2">
                <Input
                    id={rowId}
                    type="text"
                    inputMode="decimal"
                    value={draft}
                    placeholder={placeholder || ""}
                    readOnly={readOnly}
                    aria-labelledby={`${rowId}-label`}
                    aria-describedby={`${helpId} ${hasError ? errId : ""}`.trim()}
                    aria-invalid={hasError || undefined}
                    onChange={(e) => {
                        setDraft(e.target.value);
                        emit(e.target.value, activeUnit);
                    }}
                    onFocus={() => setFocused(true)}
                    onBlur={() => setFocused(false)}
                    className={[
                        "tabular-nums bg-ux-bg-2 border",
                        hasError
                            ? "border-ux-danger/60 focus-visible:ring-ux-danger/40"
                            : "border-ux-border-1",
                    ].join(" ")}
                    data-testid={`paramfield-input-${rowId}`}
                />
                <Select
                    value={activeUnit || "__none__"}
                    onValueChange={(v) => handleUnitChange(v === "__none__" ? "" : v)}
                    disabled={!showUnitSelector}
                >
                    <SelectTrigger
                        className="w-[96px] bg-ux-bg-2 border-ux-border-1 text-[12px] font-mono"
                        data-testid={`paramfield-unit-${rowId}`}
                    >
                        <SelectValue placeholder="unit" />
                    </SelectTrigger>
                    <SelectContent
                        className="bg-ux-bg-1 border-ux-border-1"
                        data-testid={`paramfield-unit-menu-${rowId}`}
                    >
                        {kindUnits
                            .filter((u) => u.id !== "")
                            .map((u) => (
                                <SelectItem
                                    key={u.id}
                                    value={u.id}
                                    className="text-[12px] font-mono"
                                    data-testid={`paramfield-unit-option-${rowId}-${u.id}`}
                                >
                                    {u.label || u.id}
                                </SelectItem>
                            ))}
                        {/* Fallback placeholder item so Radix doesn't balk on kinds
                            with a single non-SI display unit. */}
                        {kindUnits.filter((u) => u.id !== "").length === 0 && (
                            <SelectItem
                                value="__none__"
                                className="text-[12px] font-mono"
                                data-testid={`paramfield-unit-option-${rowId}-none`}
                            >
                                —
                            </SelectItem>
                        )}
                    </SelectContent>
                </Select>
            </div>

            {/* Row 3 — description + bounds strip. */}
            <div
                id={helpId}
                className="text-[11px] text-ux-text-3 font-mono flex items-center gap-2"
            >
                {description && <span className="truncate">{description}</span>}
                {bounds && (bounds.min !== undefined || bounds.max !== undefined) && (
                    <span
                        className="text-[11px] text-ux-text-3 tabular-nums ml-auto"
                        data-testid={`paramfield-bounds-${rowId}`}
                    >
                        {bounds.min !== undefined && (
                            <>min&nbsp;{toDisplay(bounds.min, activeUnit)}&nbsp;</>
                        )}
                        {bounds.max !== undefined && (
                            <>max&nbsp;{toDisplay(bounds.max, activeUnit)}</>
                        )}
                    </span>
                )}
            </div>

            {hasError && (
                <div
                    id={errId}
                    role="alert"
                    className="text-[11px] text-ux-danger flex items-center gap-1"
                    data-testid={`paramfield-error-${rowId}`}
                >
                    <AlertTriangle className="w-3 h-3" aria-hidden="true" />
                    {errorText}
                </div>
            )}
        </div>
    );
}
